import { warning } from "../lang/console.js";
import state from "./../state/state.ts";
import {
	emit,
	LOADER_COMPLETE,
	LOADER_ERROR,
	LOADER_PROGRESS,
} from "../system/event.ts";
import { getBasename } from "../utils/file.ts";
import {
	binList,
	fontList,
	gltfList,
	imgList,
	jsonList,
	mtlList,
	objList,
	shaderList,
	tmxList,
	videoList,
} from "./cache.js";
import { preloadAseprite } from "./parsers/aseprite.js";
import { preloadAudio, unloadAllAudio, unloadAudio } from "./parsers/audio.js";
import { preloadBinary } from "./parsers/binary.js";
import { preloadFontFace } from "./parsers/fontface.js";
import { preloadGLTF } from "./parsers/gltf.js";
import { preloadImage } from "./parsers/image.js";
import { preloadJSON } from "./parsers/json.js";
import { preloadMTL } from "./parsers/mtl.js";
import { preloadOBJ } from "./parsers/obj.js";
import { preloadJavascript } from "./parsers/script.js";
import { preloadShader } from "./parsers/shader.js";
import { preloadTMX } from "./parsers/tmx.js";
import { preloadVideo } from "./parsers/video.js";

/**
 * a small class to manage loading of stuff and manage resources
 * @namespace loader
 */

//  to enable/disable caching
export let nocache = "";

/**
 * @type {Object.<string, string>}
 */
export const baseURL = {};

/**
 * crossOrigin attribute to configure the CORS requests for Image and Video data element.
 * By default (that is, when the attribute is not specified), CORS is not used at all.
 * The "anonymous" keyword means that there will be no exchange of user credentials via cookies,
 * client-side SSL certificates or HTTP authentication as described in the Terminology section of the CORS specification.<br>
 * @type {string}
 * @default undefined
 * @see {@link setOptions}
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/HTML/CORS_settings_attributes}
 * @deprecated since 20.4.0, read-only — set it with
 * {@link setOptions}. This is a module binding, so assigning to it throws a
 * `TypeError`; the example below never worked.
 * @example
 *  // allow for cross-origin texture loading
 * me.loader.setOptions({ crossOrigin: "anonymous" });
 *
 * // set all resources to be loaded
 * me.loader.preload(game.resources, () => this.loaded());
 */
export let crossOrigin;

/**
 * indicates whether or not cross-site Access-Control requests should be made using credentials such as cookies,
 * authorization headers or TLS client certificates. Setting withCredentials has no effect on same-site requests.
 * @public
 * @type {boolean}
 * @see {@link setOptions}
 * @default false
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/API/XMLHttpRequest/withCredentials}
 * @deprecated since 20.4.0, read-only — set it with
 * {@link setOptions}. This is a module binding, so assigning to it throws a
 * `TypeError`; the example below never worked.
 * @example
 *  // enable withCredentials, for assets behind a session cookie or login
 * me.loader.setOptions({ withCredentials: true });
 *
 * // set all resources to be loaded
 * me.loader.preload(game.resources, () => this.loaded());
 */
export let withCredentials = false;

/**
 * enable the nocache mechanism
 * @ignore
 * @internal
 */
export function setNocache(enable = false) {
	nocache = enable ? "?" + ~~(Math.random() * 10000000) : "";
}

/**
 * Sets the options for the loader.
 * @param {Object} options - The options to set.
 * @param {string} [options.crossOrigin] - The crossOrigin attribute to configure the CORS requests for Image and Video data element.
 * @param {boolean} [options.nocache] - Enable or disable the nocache mechanism.
 * @param {boolean} [options.withCredentials] - Indicates whether or not cross-site Access-Control requests should be made using credentials.
 * @example
 * // Set the crossOrigin attribute to "anonymous"
 * me.loader.setOptions({ crossOrigin: "anonymous" });
 *
 * // Enable the nocache mechanism
 * me.loader.setOptions({ nocache: true });
 *
 * // Enable withCredentials
 * me.loader.setOptions({ withCredentials: true });
 * @category Assets
 */
export function setOptions(options) {
	if (options.crossOrigin !== undefined) {
		crossOrigin = options.crossOrigin;
	}
	if (options.nocache !== undefined) {
		setNocache(options.nocache);
	}
	if (options.withCredentials !== undefined) {
		withCredentials = options.withCredentials;
	}
}

/**
 * change the default baseURL for the given asset type.<br>
 * (this will prepend the asset URL and must finish with a '/')
 * @public
 * @param {string} type  - "*", "audio", "video", "binary", "image", "json", "js", "tmx", "tsx", "fontface", "aseprite", "shader", "obj", "mtl", "gltf", "glb"
 * @param {string} [url="./"] - default base URL
 * @example
 * // change the base URL relative address for audio assets
 * me.loader.setBaseURL("audio", "data/audio/");
 * // change the base URL absolute address for all object types
 * me.loader.setBaseURL("*", "http://myurl.com/")
 * @category Assets
 */
export function setBaseURL(type, url = "./") {
	if (type !== "*") {
		baseURL[type] = url;
	} else {
		// "wildcards"
		baseURL["audio"] = url;
		baseURL["video"] = url;
		baseURL["binary"] = url;
		baseURL["image"] = url;
		baseURL["json"] = url;
		baseURL["js"] = url;
		baseURL["tmx"] = url;
		baseURL["tsx"] = url;
		baseURL["fontface"] = url;
		baseURL["aseprite"] = url;
		baseURL["shader"] = url;
		// deliberately NOT part of the wildcard: 3D asset types resolve
		// their internal references (map_Kd textures, glTF buffers) relative
		// to their own file, so a global prefix would double-prefix them —
		// set them individually when needed
	}
}

/**
 * the completion callback most recently handed to {@link loader.preload}, so a
 * later {@link loader.load} still reports completion to it.
 *
 * Module-private on purpose: this was an exported `let`, which a module
 * namespace makes read-only — `loader.onload = fn` throws, so it never worked
 * as the settable option it was documented to be. Use the `onloadcb` parameter
 * of {@link loader.preload}, or the {@link event.LOADER_COMPLETE} event.
 * @ignore
 * @internal
 */
let onload;

/**
 * list of parser function for supported format type
 */
const parsers = new Map();

/**
 * keep track if parsers were registered
 */
let parserInitialized = false;

// flag to check loading status
let resourceCount = 0;
let loadCount = 0;

/**
 * Assets uploaded with an error
 */
const failureLoadedAssets = {};

/**
 * init all supported parsers
 * @ignore
 * @internal
 */
function initParsers() {
	setParser("binary", preloadBinary);
	setParser("image", preloadImage);
	setParser("json", preloadJSON);
	setParser("js", preloadJavascript);
	setParser("tmx", preloadTMX);
	setParser("tsx", preloadTMX);
	setParser("audio", preloadAudio);
	setParser("fontface", preloadFontFace);
	setParser("video", preloadVideo);
	setParser("obj", preloadOBJ);
	setParser("mtl", preloadMTL);
	setParser("gltf", preloadGLTF);
	setParser("glb", preloadGLTF);
	setParser("aseprite", preloadAseprite);
	setParser("shader", preloadShader);
	parserInitialized = true;
}

/**
 * Complete loading: invoke the callback and emit the LOADER_COMPLETE event.
 * @param {Function} onloadcb - the completion callback
 * @ignore
 * @internal
 */
function completeLoading(onloadcb) {
	const callback = onloadcb || onload;
	if (typeof callback === "function") {
		callback();
	}
	// always signal completion — with the Promise form of preload() a callback
	// is optional (you can `await` instead), so a missing callback is no longer
	// an error; the loading screen / consumers react to LOADER_COMPLETE.
	emit(LOADER_COMPLETE);
}

/**
 * just increment the number of already loaded resources
 * @ignore
 * @internal
 */
function onResourceLoaded(res) {
	delete failureLoadedAssets[res.src];
	// increment the loading counter
	loadCount++;

	// currrent progress
	const progress = loadCount / resourceCount;

	emit(LOADER_PROGRESS, progress, res);
}

/**
 * on error callback for image loading
 * @param {Asset} asset - asset that loaded with failure
 * @ignore
 * @internal
 */
function onLoadingError(res) {
	failureLoadedAssets[res.src] = res;
	emit(LOADER_ERROR, res);
	throw new Error("Failed loading resource " + res.src);
}

/**
 * an asset definition to be used with the loader
 * @typedef {object} Asset
 * @property {string} name - name of the asset
 * @property {string} type  - the type of the asset ("audio"|"binary"|"image"|"json"|"js"|"tmx"|"tsx"|"fontface"|"video"|"aseprite"|"shader"|"obj"|"mtl"|"gltf"|"glb"). JSON-serialised Tiled maps and tilesets (`.tmj` / `.tsj`) load under `"tmx"` / `"tsx"` — those are file extensions, not asset types.
 * @property {string|string[]} [src]  - path and/or file name of the resource (for audio assets only the path is required).
 * For image assets, an array of sources can be provided as a fallback chain (e.g. compressed texture formats by priority, with a PNG fallback).
 * The loader will try each source in order and use the first one that loads successfully.
 * @property {string|{glsl?: string, wgsl?: string}} [data]  - inline content if not provided through a src url: TMX data for "tmx" assets, GLSL source (the ShaderEffect fragment-body convention) for "shader" assets. A shader may instead carry a `{glsl, wgsl}` pair, so one asset serves both backends; either half may be omitted
 * @property {boolean} [stream=false] - Set to true to not to wait for large audio or video file to be downloaded before playing.
 * @property {boolean} [autoplay=false] - Set to true to automatically start playing audio or video when loaded or added to a scene (using autoplay might require user iteraction to enable it)
 * @property {boolean} [loop=false] - Set to true to automatically loop the audio or video when playing
 * @see {@link preload}
 * @see {@link load}
 * @example
 *   // PNG tileset
 *   {name: "tileset-platformer", type: "image",  src: "data/map/tileset.png"}
 *   // PNG packed texture
 *   {name: "texture", type:"image", src: "data/gfx/texture.png"}
 *   // PNG base64 encoded image
 *   {name: "texture", type:"image", src: "data:image/png;base64,iVBORw0KAAAQAAAAEACA..."}
 *   // compressed texture with fallback chain (tries each source in order until one succeeds)
 *   {name: "terrain", type:"image", src: ["data/gfx/terrain.astc.ktx", "data/gfx/terrain.dds", "data/gfx/terrain.png"]}
 *   // TSX file
 *   {name: "meta_tiles", type: "tsx", src: "data/map/meta_tiles.tsx"}
 *   // TMX level (XML & JSON)
 *   {name: "map1", type: "tmx", src: "data/map/map1.json"}
 *   {name: "map2", type: "tmx", src: "data/map/map2.tmx"}
 *   {name: "map3", type: "tmx", format: "json", data: {"height":15,"layers":[...],"tilewidth":32,"version":1,"width":20}}
 *   {name: "map4", type: "tmx", format: "xml", data: {xml representation of tmx}}
 *   // audio resources
 *   {name: "bgmusic", type: "audio",  src: "data/audio/"}
 *   {name: "cling",   type: "audio",  src: "data/audio/"}
 *   // base64 encoded audio resources
 *   {name: "band",   type: "audio",  src: "data:audio/wav;base64,..."}
 *   // binary file
 *   {name: "ymTrack", type: "binary", src: "data/audio/main.ym"}
 *   // JSON file (used for texturePacker)
 *   {name: "texture", type: "json", src: "data/gfx/texture.json"}
 *   // JavaScript file
 *   {name: "plugin", type: "js", src: "data/js/plugin.js"}
 *   // Font Face
 *   { name: "'kenpixel'", type: "fontface",  src: "data/font/kenvector_future.woff2" }
 *   // video resources
 *   {name: "intro", type: "video",  src: "data/video/"}
 *   // 3D assets: Wavefront OBJ model + MTL material library
 *   {name: "ship",  type: "obj", src: "data/models/ship.obj"}
 *   {name: "ship",  type: "mtl", src: "data/models/ship.mtl"}
 *   // glTF / GLB scene (auto-registers with the level director)
 *   {name: "diorama", type: "glb", src: "data/scenes/diorama.glb"}
 *   // shader assets: an effect body, or a complete dual-backend program
 *   {name: "flash", type: "shader", src: "shaders/flash.frag"}
 *   {name: "toon",  type: "shader", src: {vertex: "shaders/toon.vert", fragment: "shaders/toon.frag", wgsl: "shaders/toon.wgsl"}}
 */

/**
 * specify a parser/preload function for the given asset type
 * @param {string} type - asset type
 * @param {function} parserFn - parser function
 * @see {@link Asset.type}
 * @example
 * // specify a custom function for "abc" format
 * function customAbcParser(data, onload, onerror) {
 *    // preload and do something with the data
 *    let parsedData = doSomething(data);
 *    // when done, call the onload callback with the parsed data
 *    onload(parsedData);
 *    // in case of error, call the onerror callback
 *    onerror();
 *    // return the amount of asset parsed
 *    return 1
 * }
 * // set the parser for the custom format
 * loader.setParser("abc", customAbcParser);
 * @category Assets
 */
export function setParser(type, parserFn) {
	if (typeof parserFn !== "function") {
		throw new Error("invalid parser function for " + type);
	}

	if (typeof parsers.get(type) !== "undefined") {
		warning("overriding parser for " + type + " format");
	}

	parsers.set(type, parserFn);
}

/**
 * set all the specified game assets to be preloaded.
 * @param {Asset[]} assets - list of assets to load
 * @param {Function} [onloadcb=loader.onload] - function to be called when all resources are loaded
 * @param {boolean} [switchToLoadState=true] - automatically switch to the loading screen
 * @returns {Promise<void>} resolves once every asset has loaded (rejects on a
 * load failure). The `onloadcb` callback is still invoked on success, so both
 * the callback and `await` forms work — use whichever you prefer.
 * @example
 * game.assets = [
 *   // PNG tileset
 *   {name: "tileset-platformer", type: "image",  src: "data/map/tileset.png"},
 *   // PNG packed texture
 *   {name: "texture", type:"image", src: "data/gfx/texture.png"}
 *   // PNG base64 encoded image
 *   {name: "texture", type:"image", src: "data:image/png;base64,iVBORw0KAAAQAAAAEACA..."},
 *   // compressed texture with fallback chain (tries each source in order until one succeeds)
 *   {name: "terrain", type:"image", src: ["data/gfx/terrain.astc.ktx", "data/gfx/terrain.dds", "data/gfx/terrain.png"]},
 *   // TSX file
 *   {name: "meta_tiles", type: "tsx", src: "data/map/meta_tiles.tsx"},
 *   // TMX level (XML & JSON)
 *   {name: "map1", type: "tmx", src: "data/map/map1.json"},
 *   {name: "map2", type: "tmx", src: "data/map/map2.tmx"},
 *   {name: "map3", type: "tmx", format: "json", data: {"height":15,"layers":[...],"tilewidth":32,"version":1,"width":20}},
 *   {name: "map4", type: "tmx", format: "xml", data: {xml representation of tmx}},
 *   // audio resources
 *   {name: "bgmusic", type: "audio",  src: "data/audio/"},
 *   {name: "cling",   type: "audio",  src: "data/audio/"},
 *   // base64 encoded audio resources
 *   {name: "band",   type: "audio",  src: "data:audio/wav;base64,..."},
 *   // binary file
 *   {name: "ymTrack", type: "binary", src: "data/audio/main.ym"},
 *   // JSON file (used for texturePacker)
 *   {name: "texture", type: "json", src: "data/gfx/texture.json"},
 *   // JavaScript file
 *   {name: "plugin", type: "js", src: "data/js/plugin.js"},
 *   // Font Face
 *   {name: "'kenpixel'", type: "fontface",  src: "data/font/kenvector_future.woff2"},
 *   // video resources
 *   {name: "intro", type: "video",  src: "data/video/"},
 *   // base64 encoded video asset
 *   me.loader.load({name: "avatar", type:"video", src: "data:video/mp4;base64,AAAAIGZ0eXBpc29tAAACAGlzb21pc28yYXZjMW1wNDEAAAAIZnJlZ..."};
 * ];
 * ...
 * // set all resources to be loaded (callback form)
 * me.loader.preload(game.assets, () => this.loaded());
 * // ...or await it (the callback still fires too)
 * await me.loader.preload(game.assets);
 * @category Assets
 */
export function preload(assets, onloadcb, switchToLoadState = true) {
	// set the onload callback if defined
	if (typeof onloadcb !== "undefined") {
		onload = onloadcb;
	}

	// parse the resources and collect a promise for each asset. `load()` now
	// returns a Promise in its no-callback form, so there's no need to wrap a
	// callback in `new Promise` here — just layer the progress / error reporting
	// on top of it. Each asset counts as one toward the progress total (parsers
	// load 0 or 1 resource; a cached asset resolves immediately and still
	// reports progress, keeping `loadCount`/`resourceCount` balanced).
	const promises = assets.map((asset) => {
		resourceCount += 1;
		return load(asset)
			.then(() => {
				onResourceLoaded(asset);
			})
			.catch((err) => {
				// onLoadingError emits LOADER_ERROR and throws by design, so this
				// re-rejects → Promise.all (and the returned promise) reject and
				// `await loader.preload(...)` surfaces the failure. The trailing
				// throw keeps the rejection meaningful should it ever stop throwing.
				onLoadingError(asset);
				throw err;
			});
	});

	if (switchToLoadState === true) {
		// switch to the loading screen
		state.change(state.LOADING);
	}

	// Resolve once every asset has loaded; call the completion callback on
	// success (back-compat). Returned so callers can `await loader.preload(...)`
	// instead of (or as well as) passing a callback.
	return Promise.all(promises).then(() => {
		completeLoading(onload);
	});
}

/**
 * retry loading assets after a loading failure
 * @param {string} src - src of asset to reload
 * @example
 *  event.on(
 *      event.LOADER_ERROR,
 *      (res) => {
 *          // custom function
 *          showErrorNotification({
 *              text: `Error during loading content: ${res.name}`,
 *              done: loader.reload(res.src);
 *          })
 *      }
 *  );
 **/
export function reload(src) {
	const assetToReload = failureLoadedAssets[src];
	unload(assetToReload);
	resourceCount -= 1;

	new Promise((resolve, reject) => {
		const count = load(
			assetToReload,
			() => {
				onResourceLoaded(assetToReload);
				resolve();
			},
			(err) => {
				onLoadingError(assetToReload);
				reject(err);
			},
		);
		resourceCount += count;
		if (count === 0) {
			resolve();
		}
	}).then(() => {
		completeLoading(onload);
	});
}

/**
 * Load a single asset (to be used if you need to load additional asset(s) during the game)
 * @param {Asset} asset
 * @param {Function} [onload] - function to be called when the asset is loaded
 * @param {Function} [onerror] - function to be called in case of error
 * @returns {number|Promise<void>} with `onload`/`onerror` provided, the amount
 * of corresponding resource to be preloaded (the legacy callback form). With
 * **both omitted**, a Promise that resolves once the asset has loaded, so you
 * can `await loader.load(asset)` for a one-off dynamic load.
 * @example
 * // load an image asset (callback form)
 * me.loader.load({name: "avatar",  type:"image",  src: "data/avatar.png"}, () => this.onload(), () => this.onerror());
 * // ...or await a single dynamic asset (no callbacks)
 * await me.loader.load({name: "avatar", type: "image", src: "data/avatar.png"});
 * // load a compressed texture with fallback chain
 * me.loader.load({name: "terrain", type:"image", src: ["data/gfx/terrain.astc.ktx", "data/gfx/terrain.dds", "data/gfx/terrain.png"]}, () => this.onload());
 * // load a base64 image asset
 *  me.loader.load({name: "avatar", type:"image", src: "data:image/png;base64,iVBORw0KAAAQAAAAEACA..."};
 *  // load a base64 video asset
 *  me.loader.load({
 *     name: "avatar",
 *     type:"video",
 *     src: "data:video/mp4;base64,AAAAIGZ0eXBpc29tAAACAGlzb21pc28yYXZjMW1wNDEAAAAIZnJlZ.."
 *  };
 * // start loading music
 * me.loader.load({
 *     name   : "bgmusic",
 *     type   : "audio",
 *     src    : "data/audio/"
 * }, function () {
 *     me.audio.play("bgmusic");
 * });
 * @category Assets
 */
export function load(asset, onload, onerror) {
	// make sure all parsers have been initialized
	if (parserInitialized === false) {
		initParsers();
	}

	// Resolve the effective src WITHOUT mutating the caller's asset
	// descriptor: load() used to write the transformed url back into
	// asset.src, so retrying the same object — loader.reload() after a
	// failure, or simply load()ing the same manifest entry twice —
	// prepended baseURL onto an already-transformed src and fetched a
	// mangled URL. Caches, events and failure tracking all key on the
	// caller's original src; only the parser sees the resolved one.
	let src = asset.src;

	// strip url() wrapper for fontface assets so baseURL can be prepended to the raw path
	if (asset.type === "fontface" && typeof src === "string") {
		const urlMatch = src.match(/^url\(\s*['"]?(.*?)['"]?\s*\)$/);
		if (urlMatch) {
			src = urlMatch[1];
		}
	}

	// transform the url if necessary (skip for local() font sources and data URIs)
	if (
		typeof baseURL[asset.type] !== "undefined" &&
		typeof src === "string" &&
		!src.startsWith("local(") &&
		!src.startsWith("data:")
	) {
		src = baseURL[asset.type] + src;
	}

	const resource =
		src === asset.src ? asset : Object.assign({}, asset, { src });

	const parser = parsers.get(asset.type);

	if (typeof parser === "undefined") {
		throw new Error("load : unknown or invalid resource type : " + asset.type);
	}

	const settings = {
		nocache: nocache,
		crossOrigin: crossOrigin,
		withCredentials: withCredentials,
	};

	// Promise form: with no callbacks, resolve once the asset (and any
	// sub-resources) have loaded — for a one-off `await loader.load(asset)`.
	// The legacy callback form below still returns the resource count.
	if (onload === undefined && onerror === undefined) {
		return new Promise((resolve, reject) => {
			// parser returns the amount of asset to be loaded (usually 1, more
			// if it splits into several); 0 means already cached → resolve now.
			const count = parser.call(
				this,
				resource,
				() => {
					resolve();
				},
				reject,
				settings,
			);
			if (count === 0) {
				resolve();
			}
		});
	}

	// parser returns the amount of asset to be loaded (usually 1 unless an asset is splitted into several ones)
	return parser.call(this, resource, onload, onerror, settings);
}

/**
 * unload the specified asset to free memory
 * @param {Asset} asset
 * @returns {boolean} true if unloaded
 * @example me.loader.unload({name: "avatar",  type:"image"});
 * @category Assets
 */
export function unload(asset) {
	switch (asset.type) {
		case "binary":
			if (!(asset.name in binList)) {
				return false;
			}

			delete binList[asset.name];
			return true;

		case "image":
			if (!(asset.name in imgList)) {
				return false;
			}
			delete imgList[asset.name];
			return true;

		case "json":
			if (!(asset.name in jsonList)) {
				return false;
			}

			delete jsonList[asset.name];
			return true;

		case "js":
			// ??
			return true;

		case "fontface":
			// membership guard like every other type — FontFaceSet.delete is
			// WebIDL-typed and THROWS on undefined instead of returning false
			if (!(asset.name in fontList)) {
				return false;
			}
			if (
				typeof globalThis.document !== "undefined" &&
				typeof globalThis.document.fonts !== "undefined"
			) {
				globalThis.document.fonts.delete(fontList[asset.name]);
				delete fontList[asset.name];
				return true;
			}
			return false;

		case "tmx":
		case "tsx":
			if (!(asset.name in tmxList)) {
				return false;
			}

			delete tmxList[asset.name];
			return true;

		case "audio":
			return unloadAudio(asset.name);

		case "video":
			if (!(asset.name in videoList)) {
				return false;
			}

			delete videoList[asset.name];
			return true;

		case "obj":
			if (!(asset.name in objList)) {
				return false;
			}

			delete objList[asset.name];
			return true;

		case "gltf":
		case "glb":
			if (!(asset.name in gltfList)) {
				return false;
			}

			delete gltfList[asset.name];
			return true;

		case "mtl":
			if (!(asset.name in mtlList)) {
				return false;
			}

			delete mtlList[asset.name];
			return true;

		case "aseprite": {
			// aseprite asset populates both imgList and jsonList under the same key
			const hadImage = asset.name in imgList;
			const hadJson = asset.name in jsonList;
			if (!hadImage && !hadJson) {
				return false;
			}
			delete imgList[asset.name];
			delete jsonList[asset.name];
			return true;
		}

		case "shader": {
			const effect = shaderList[asset.name];
			if (typeof effect === "undefined") {
				return false;
			}
			// the loader owns the shared ShaderEffect/GLShader — actually free
			// the GL program, don't just drop the cache entry. A `null` entry
			// is a {vertex, fragment} pair loaded under the Canvas renderer
			// (no GL program to free).
			effect?.destroy();
			delete shaderList[asset.name];
			return true;
		}

		default:
			throw new Error(
				"unload : unknown or invalid resource type : " + asset.type,
			);
	}
}

/**
 * unload all resources to free memory
 * @example me.loader.unloadAll();
 * @category Assets
 */
export function unloadAll() {
	let name;

	// unload all binary resources
	for (name in binList) {
		if (binList.hasOwnProperty(name)) {
			unload({
				name: name,
				type: "binary",
			});
		}
	}

	// unload all image resources
	for (name in imgList) {
		if (imgList.hasOwnProperty(name)) {
			unload({
				name: name,
				type: "image",
			});
		}
	}

	// unload all tmx resources
	for (name in tmxList) {
		if (tmxList.hasOwnProperty(name)) {
			unload({
				name: name,
				type: "tmx",
			});
		}
	}

	// unload all json resources
	for (name in jsonList) {
		if (jsonList.hasOwnProperty(name)) {
			unload({
				name: name,
				type: "json",
			});
		}
	}

	// unload all video resources
	for (name in videoList) {
		if (videoList.hasOwnProperty(name)) {
			unload({
				name: name,
				type: "video",
			});
		}
	}

	// unload all font resources
	for (name in fontList) {
		if (fontList.hasOwnProperty(name)) {
			unload({
				name: name,
				type: "fontface",
			});
		}
	}

	// unload all OBJ resources
	for (name in objList) {
		if (objList.hasOwnProperty(name)) {
			unload({
				name: name,
				type: "obj",
			});
		}
	}

	// unload all MTL resources
	for (name in mtlList) {
		if (mtlList.hasOwnProperty(name)) {
			unload({
				name: name,
				type: "mtl",
			});
		}
	}

	// unload all glTF/GLB scene resources
	for (name in gltfList) {
		if (gltfList.hasOwnProperty(name)) {
			unload({
				name: name,
				type: "glb",
			});
		}
	}

	// unload all shader resources (destroys their shared GL programs)
	for (name in shaderList) {
		if (shaderList.hasOwnProperty(name)) {
			unload({
				name: name,
				type: "shader",
			});
		}
	}

	// unload all audio resources
	unloadAllAudio();
}

/**
 * return the specified TMX/TSX object
 * @param {string} elt - name of the tmx/tsx element ("map1");
 * @returns {object} requested element or null if not found
 * @category Assets
 */
export function getTMX(elt) {
	// force as string
	elt = "" + elt;
	if (elt in tmxList) {
		return tmxList[elt];
	}
	return null;
}

/**
 * return the specified Binary object
 * @param {string} elt - name of the binary object ("ymTrack");
 * @returns {object} requested element or null if not found
 * @category Assets
 */
export function getBinary(elt) {
	// force as string
	elt = "" + elt;
	if (elt in binList) {
		return binList[elt];
	}
	return null;
}

/**
 * return the specified Image Object
 * @param {string} image - name of the Image element ("tileset-platformer");
 * @returns {HTMLImageElement|CompressedImage|null} requested element or null if not found
 * @category Assets
 */
export function getImage(image) {
	// force as string and extract the base name
	image = getBasename("" + image);
	if (image in imgList) {
		// return the corresponding Image object
		return imgList[image];
	}
	return null;
}

/**
 * return the specified JSON Object
 * @param {string} elt - name of the json file
 * @returns {JSON}
 * @category Assets
 */
export function getJSON(elt) {
	// force as string
	elt = "" + elt;
	if (elt in jsonList) {
		return jsonList[elt];
	}
	return null;
}

/**
 * return the specified OBJ model data
 * @param {string} elt - name of the OBJ file (as specified in the preload list)
 * @returns {object} parsed OBJ data with `vertices` (Float32Array), `uvs` (Float32Array), `indices` (Uint16Array), and `vertexCount` (number), or null if not found
 * @category Assets
 * @example
 * // 1. preload the OBJ model and its texture
 * me.loader.preload([
 *     { name: "cube", type: "obj", src: "models/cube.obj" },
 *     { name: "cube", type: "image", src: "models/cube_texture.png" },
 * ], () => {
 *     // 2. create a Mesh using the preloaded model name
 *     const mesh = new me.Mesh(400, 300, {
 *         model: "cube",        // references the preloaded OBJ
 *         texture: "cube",      // references the preloaded image
 *         width: 200,
 *         height: 200,
 *     });
 *     me.game.world.addChild(mesh);
 *
 *     // 3. or access the raw parsed data directly
 *     const data = me.loader.getOBJ("cube");
 *     // data.vertices — Float32Array of x,y,z positions
 *     // data.uvs — Float32Array of u,v texture coordinates
 *     // data.indices — Uint16Array of triangle vertex indices
 *     // data.vertexCount — number of unique vertices
 *     // data.groups — usemtl material groups ({materialName, start, count} index ranges)
 * });
 */
export function getOBJ(elt) {
	// force as string
	elt = "" + elt;
	if (elt in objList) {
		return objList[elt];
	}
	return null;
}

/**
 * One mesh primitive out of a parsed glTF/GLB scene.
 *
 * Spelled out rather than left as `object` so the geometry can be read from
 * TypeScript — feeding `vertices`/`uvs`/`normals`/`indices` straight into a
 * {@link Mesh} or {@link InstancedMesh} is the whole point of exposing it.
 * @typedef {object} GLTFNode
 * @property {number[]} world - accumulated world transform, 16 floats, column-major
 * @property {Float32Array} vertices - positions, x,y,z triplets
 * @property {Float32Array} normals - per-vertex normals
 * @property {Float32Array} uvs - texture coordinates, u,v pairs
 * @property {Uint16Array|Uint32Array} indices - triangle vertex indices
 * @property {number} vertexCount - number of vertices
 * @property {HTMLImageElement|null} image - decoded baseColor texture, or `null`
 * @property {number[]} [baseColorFactor] - material baseColor factor, `[r, g, b, a]`
 * @property {Uint32Array} [colors] - per-vertex colour, packed RGBA8
 * @property {string} [textureRepeat] - wrap mode derived from the glTF sampler
 * @property {string} [textureFilter] - magnification filter derived from the glTF sampler
 * @property {number} [alphaCutoff] - cutout threshold from `alphaMode: "MASK"`
 * @property {number[]} [emissive] - emissive factor, `[r, g, b]`
 * @property {boolean} [unlit] - the material carried `KHR_materials_unlit`
 * @property {boolean} [doubleSided] - the material is double-sided
 * @property {string} [name] - the source node's name
 */

/**
 * a parsed glTF/GLB scene descriptor, as returned by {@link loader.getGLTF}
 * @typedef {object} GLTFData
 * @property {GLTFNode[]} nodes - one entry per mesh primitive
 * @property {Array<{world: number[], type?: string, perspective?: {yfov?: number, aspectRatio?: number, znear?: number, zfar?: number}, orthographic?: object}>} cameras - glTF cameras, each with its `world` transform + the glTF camera parameters (`perspective` for perspective cameras, `orthographic` otherwise)
 * @property {object[]} lights - parsed `KHR_lights_punctual` lights (`type`, `color`, `intensity`, `range`, `innerConeAngle`/`outerConeAngle` for spots, world-space `direction`/`position`, `name`)
 * @property {{min: number[], max: number[]}} bounds - world-space scene bounds in glTF units
 * @property {object[]} graph - the full node graph (every node's TRS/matrix + children), for custom traversal
 * @property {object[]} animations - parsed node animations (consumed by `GLTFModel` playback)
 */

/**
 * return the parsed glTF/GLB scene descriptor for the given asset name.
 *
 * The descriptor is `{ nodes, cameras, lights, bounds, graph, animations }`:
 * - `nodes` — one entry per mesh primitive, each carrying its accumulated
 *   `world` transform (16 floats, column-major), `vertices`, `normals`,
 *   `uvs`, `indices`, `vertexCount`, a decoded baseColor `image` (or `null`),
 *   and a `doubleSided` flag.
 * - `cameras` — glTF cameras, each with its `world` transform + perspective
 *   parameters.
 * - `lights` — parsed `KHR_lights_punctual` lights (`type`, `color`,
 *   `intensity`, `range`, spot cone angles, world-space
 *   `direction`/`position`, `name`); empty without the extension. The
 *   level director instantiates directional, point and spot lights
 *   automatically (see {@link level.load} options).
 * - `bounds` — world-space `{ min, max }` (glTF units), handy for framing.
 *
 * Most code never needs this: a preloaded glTF/GLB auto-registers with the
 * {@link level} director, so the whole scene loads into a container in one
 * call via `me.level.load(name)` — exactly like a Tiled map. Reach for
 * `getGLTF` only when you want to inspect the raw descriptor (e.g. to frame
 * a `Camera3d` from the embedded camera).
 * @param {string} elt - name of the glTF/GLB file (as specified in the preload list)
 * @returns {GLTFData|null} the parsed scene descriptor, or `null` if not found
 * @category Assets
 * @example
 * me.loader.preload(
 *     [{ name: "diorama", type: "glb", src: "scenes/diorama.glb" }],
 *     () => {
 *         // load the whole scene into the world (view under a Camera3d)
 *         me.level.load("diorama", { scale: 32 });
 *
 *         // ...or inspect the raw descriptor for custom framing
 *         const scene = me.loader.getGLTF("diorama");
 *         const { min, max } = scene.bounds;
 *     },
 * );
 */
export function getGLTF(elt) {
	elt = "" + elt;
	if (elt in gltfList) {
		return gltfList[elt];
	}
	return null;
}

/**
 * Return the precompiled `ShaderEffect` for the given "shader" asset —
 * compiled once during preloading, ready to assign to a renderable or
 * camera `shader` property.
 *
 * **This returns a SHARED instance**: the *same* `ShaderEffect` object on
 * every call, owned by the loader (its `shared` flag is `true`). That means:
 * - it is safe to assign to any number of renderables — none of their
 *   cleanup paths will auto-destroy it, only {@link loader.unload} /
 *   {@link loader.unloadAll} free it (and its GL program);
 * - all of them share ONE set of uniform values — `setUniform` on it
 *   affects every renderable using the shader.
 *
 * When a renderable needs its **own** uniform values, make a private,
 * caller-owned copy with `ShaderEffect.clone()` — the clone's `shared` flag
 * is reset to `false`, so it is auto-destroyed with the renderable it is
 * assigned to, like any hand-constructed effect.
 *
 * A shader asset declared as a **complete program** — a
 * `{vertex, fragment}` GLSL pair and/or a full `wgsl` module (see the
 * example) — compiles into a raw {@link GLShader} instead, carrying one
 * realization per GPU backend (`isWebGL` / `isWebGPU`): the type the
 * hosted paths take directly (a `Mesh` custom shader,
 * `renderer.customShader`, a custom batcher). Same shared-instance
 * semantics, and `GLShader.clone()` likewise yields a caller-owned copy.
 *
 * Degradation is never fatal: a fragment-body asset without a body in the
 * active renderer's language (or on Canvas) is an inert `ShaderEffect`
 * stub, and a complete-program asset without a realization for the active
 * backend is an inert `GLShader` — assigning either just keeps the
 * built-in rendering. Note that shader assets require an initialized
 * Application (`await app.init()`) — an inherent precondition of the
 * preload flow, since the loading screen itself needs the renderer.
 * @param {string} elt - name of the shader asset (as specified in the preload list)
 * @returns {ShaderEffect|GLShader|null} the shared, precompiled shader, or `null` if not found
 * @category Assets
 * @example
 * me.loader.preload([
 *     // from a file (or data: URI)
 *     { name: "waterRipple", type: "shader", src: "shaders/waterRipple.frag" },
 *     // or inline GLSL via the `data` field
 *     { name: "flash", type: "shader", data: `
 *         uniform float uIntensity;
 *         vec4 apply(vec4 color, vec2 uv) { return mix(color, vec4(1.0), uIntensity); }
 *     ` },
 *     // or a complete program — a {vertex, fragment} GLSL pair and/or a
 *     // full WGSL module → one GLShader carrying both realizations; the
 *     // active renderer hosts the one it speaks
 *     { name: "toonMesh", type: "shader", src: {
 *         vertex: "shaders/toon.vert",
 *         fragment: "shaders/toon.frag",
 *         wgsl: "shaders/toon.wgsl",
 *     } },
 * ], () => {
 *     // one shared program — same uniform state for every user
 *     mySprite.addPostEffect(me.loader.getShader("waterRipple"));
 *     // private copy with its own uniforms (caller-owned, shared = false)
 *     boss.addPostEffect(me.loader.getShader("flash").clone());
 *     // a complete program hosts on a mesh, replacing the built-in shading
 *     myMesh.addPostEffect(me.loader.getShader("toonMesh"));
 * });
 */
export function getShader(elt) {
	elt = "" + elt;
	if (elt in shaderList) {
		return shaderList[elt];
	}
	return null;
}

/**
 * return the specified MTL material data
 * @param {string} elt - name of the MTL file (as specified in the preload list)
 * @returns {object} map of material names to properties (`Kd`, `d`, `map_Kd`), or null if not found
 * @category Assets
 * @example
 * // 1. preload OBJ + MTL + texture
 * me.loader.preload([
 *     { name: "fox", type: "obj", src: "models/fox.obj" },
 *     { name: "fox", type: "mtl", src: "models/fox.mtl" },
 *     { name: "colormap", type: "image", src: "models/colormap.png" },
 * ], () => {
 *     // 2. create a Mesh with material — texture, tint, opacity auto-applied
 *     const mesh = new me.Mesh(400, 300, {
 *         model: "fox",
 *         material: "fox",
 *         texture: "colormap",
 *         width: 200,
 *         height: 200,
 *     });
 *
 *     // 3. or access the raw material data directly
 *     const materials = me.loader.getMTL("fox");
 *     // materials["colormap"].Kd — [r, g, b] diffuse color (0-1 range)
 *     // materials["colormap"].d — opacity (0-1)
 *     // materials["colormap"].Ke — [r, g, b] emissive color (glow, applied as Mesh.emissive)
 *     // materials["colormap"].map_Kd — resolved texture URL
 * });
 */
export function getMTL(elt) {
	elt = "" + elt;
	if (elt in mtlList) {
		return mtlList[elt];
	}
	return null;
}

/**
 * return the specified Video Object
 * @param {string} elt - name of the video file
 * @returns {HTMLVideoElement}
 * @category Assets
 */
export function getVideo(elt) {
	// force as string
	elt = "" + elt;
	if (elt in videoList) {
		return videoList[elt];
	}
	return null;
}

/**
 * return the specified FontFace Object
 * @param {string} elt - name of the font file
 * @returns {FontFace}
 * @category Assets
 */
export function getFont(elt) {
	// force as string
	elt = "" + elt;
	if (elt in fontList) {
		return fontList[elt];
	}
	return null;
}
